// Firebase Setup Utilities
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';

/**
 * Test Firebase connection and create initial data if needed
 */
export const testFirebaseConnection = async (companyId) => {
  try {
    console.log('🔄 Testing Firebase connection...');
    
    // Test basic connection by creating a test document
    const testRef = collection(db, 'reportTemplates');
    const testDoc = {
      name: 'Test Template',
      companyId: companyId,
      type: 'shipments',
      fields: {
        no: { enabled: true, label: 'No.', order: 1 },
        truckPlate: { enabled: true, label: 'Truck Plate', order: 2 }
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isDefault: true,
      test: true // Mark as test document
    };
    
    const docRef = await addDoc(testRef, testDoc);
    console.log('✅ Firebase connection successful, test document created:', docRef.id);
    
    return { success: true, testDocId: docRef.id };
  } catch (error) {
    console.error('❌ Firebase connection test failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create Firestore indexes programmatically (for development)
 * Note: In production, indexes should be created via Firebase Console or CLI
 */
export const createFirestoreIndexes = () => {
  console.log('📋 Firestore indexes needed:');
  console.log('Collection: reportTemplates');
  console.log('Fields: companyId (Ascending), createdAt (Descending)');
  console.log('');
  console.log('To create this index, run:');
  console.log('firebase firestore:indexes');
  console.log('');
  console.log('Or create manually in Firebase Console:');
  console.log('1. Go to Firestore Database');
  console.log('2. Click on "Indexes" tab');
  console.log('3. Click "Create Index"');
  console.log('4. Collection ID: reportTemplates');
  console.log('5. Add fields: companyId (Ascending), createdAt (Descending)');
};

/**
 * Initialize report templates collection with proper structure
 */
export const initializeReportTemplates = async (companyId, userId) => {
  try {
    console.log('🔄 Initializing report templates collection...');
    
    const templatesRef = collection(db, 'reportTemplates');
    const defaultTemplate = {
      name: 'Default Shipments Report',
      description: 'Standard shipment report with basic fields',
      type: 'shipments',
      companyId: companyId,
      createdBy: userId,
      fields: {
        no: { enabled: true, label: 'No.', order: 1 },
        shipmentNumber: { enabled: true, label: 'Shipment #', order: 2 },
        date: { enabled: true, label: 'Date', order: 3 },
        truckPlate: { enabled: true, label: 'Truck Plate', order: 4 },
        truckKind: { enabled: true, label: 'Truck Kind', order: 5 },
        pickupSite: { enabled: true, label: 'Pickup Site', order: 6 },
        dropoffSite: { enabled: true, label: 'Dropoff Site', order: 7 },
        numberOfStops: { enabled: true, label: 'Number of Stops', order: 8 },
        originCity: { enabled: true, label: 'Origin City', order: 9 },
        destinationCities: { enabled: true, label: 'Destination Cities', order: 10 },
        backhaulPrice: { enabled: true, label: 'Backhaul Price', order: 11 },
        itemizedCosts: { enabled: true, label: 'Itemized Costs', order: 12 },
        basePrice: { enabled: true, label: 'Base Price', order: 13 },
        totalPrice: { enabled: true, label: 'Total Price', order: 14 },
        clientName: { enabled: true, label: 'Client Name', order: 15 },
        driverName: { enabled: true, label: 'Driver Name', order: 16 },
        status: { enabled: true, label: 'Status', order: 17 },
        paymentStatus: { enabled: true, label: 'Payment Status', order: 18 },
        notes: { enabled: true, label: 'Notes', order: 19 }
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      isDefault: true
    };
    
    const docRef = await addDoc(templatesRef, defaultTemplate);
    console.log('✅ Default template created successfully:', docRef.id);
    
    return { success: true, templateId: docRef.id };
  } catch (error) {
    console.error('❌ Failed to initialize report templates:', error);
    return { success: false, error: error.message };
  }
};